import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { LogoMark, LogoText } from "@/components/Logo";
import { InstallAppButton } from "@/components/InstallAppButton";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";

function Field({ label, id, type = "text", value, onChange, placeholder, testId, autoComplete }) {
  const [show, setShow] = useState(false);
  const isPass = type === "password";
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1.5 block text-xs font-medium ga-muted">{label}</span>
      <div className="relative">
        <input id={id} type={isPass && show ? "text" : type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="ga-input" data-testid={testId} autoComplete={autoComplete} required />
        {isPass && (
          <button type="button" onClick={() => setShow(!show)} className="ga-muted absolute right-3 top-1/2 -translate-y-1/2" aria-label={show ? "Ocultar senha" : "Mostrar senha"}>
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
    </label>
  );
}

export default function AuthPage({ mode: initialMode = "login" }) {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("token") ? "reset" : initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", terms: false, privacy: false, username: "", noRecovery: false, recoveryCode: "" });
  const [kind, setKind] = useState("email"); // email | phone | both | username
  const [userAvail, setUserAvail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [demoToken, setDemoToken] = useState(null);
  const [notice, setNotice] = useState(null);
  const { login, register, registerUsername } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const res = await login(form.email, form.password);
        if (res?.two_factor_required) { navigate("/verificacao-2fa", { state: { challenge: res.challenge, hint: res.email_hint, email_sent: res.email_sent } }); return; }
        navigate("/inicio");
      } else if (mode === "register" && kind === "username") {
        if (!form.terms || !form.privacy) return toast.error("Aceite os termos e a política de privacidade.");
        if (!form.noRecovery) return toast.error("Confirme que entendeu o aviso sobre recuperação de acesso.");
        if (form.password !== form.confirm) return toast.error("As senhas não coincidem.");
        sessionStorage.setItem("ga_suggest_codes", "1");
        await registerUsername({ username: form.username, password: form.password, password_confirm: form.confirm, accepted_terms: form.terms, accepted_privacy: form.privacy, accepted_no_recovery: form.noRecovery });
        toast.success("Conta criada! Bem-vindo ao Clicou, Guardou!");
        navigate("/inicio", { state: { suggest_recovery_codes: true } });
      } else if (mode === "register") {
        if (!form.terms || !form.privacy) return toast.error("Aceite os termos e a política de privacidade.");
        await register({ name: form.name, email: form.email, password: form.password, accepted_terms: form.terms, accepted_privacy: form.privacy });
        toast.success("Bem-vindo ao Clicou, Guardou!! Confirme seu e-mail para continuar.");
        navigate("/verificar-email");
      } else if (mode === "recovery") {
        if (form.password !== form.confirm) return toast.error("As senhas não coincidem.");
        await api.post("/auth/recovery-codes/reset-password", { username: form.username, recovery_code: form.recoveryCode, new_password: form.password });
        toast.success("Senha redefinida. Entre com a nova senha.");
        setForm((f) => ({ ...f, password: "", confirm: "", recoveryCode: "" }));
        setMode("login");
      } else if (mode === "forgot") {
        setNotice(null);
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setNotice({ ok: true, text: data.message });
        if (data.demo_token) setDemoToken(data.demo_token);
      } else if (mode === "reset") {
        if (form.password !== form.confirm) return toast.error("As senhas não coincidem.");
        await api.post("/auth/reset-password", { token: params.get("token") || demoToken, password: form.password });
        toast.success("Senha redefinida. Entre com a nova senha.");
        setForm((f) => ({ ...f, password: "", confirm: "" }));
        navigate("/entrar", { replace: true });
      }
    } catch (err) {
      if (mode === "forgot") setNotice({ ok: false, text: errorMessage(err) });
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const titles = { login: "Que bom te ver de novo.", register: "Crie sua conta.", forgot: "Recuperar senha", reset: "Nova senha", recovery: "Usar código de recuperação" };
  const isUserReg = mode === "register" && kind === "username";
  const checkUsername = async (v) => {
    set("username")(v);
    if (v.length < 3) return setUserAvail(null);
    try { const { data } = await api.get("/auth/username-available", { params: { username: v } }); setUserAvail(data.available); } catch { setUserAvail(null); }
  };
  const kinds = [
    { id: "email", label: "Com e-mail e senha" },
    { id: "phone", label: "Com número de celular e senha", soon: true },
    { id: "both", label: "Com e-mail, celular e senha", soon: true },
    { id: "username", label: "Somente com nome de usuário e senha" },
  ];

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col" style={{ background: "var(--ga-bg)" }}>
      <div className="flex flex-col items-center px-7 pb-8 pt-12 text-white" style={{ background: "linear-gradient(180deg,#05324A,#0A6373)", borderRadius: "0 0 36px 36px" }}>
        <LogoMark size={72} />
        <div className="mt-4 text-3xl"><LogoText light /></div>
        <p className="mt-1 text-xs text-white/70">Você vive, a gente lembra.</p>
      </div>

      <form onSubmit={submit} className="ga-fade-up flex-1 space-y-4 px-6 pt-8" data-testid={`auth-form-${mode}`}>
        <h1 className="text-2xl font-semibold tracking-tight">{titles[mode]}</h1>

        {mode === "register" && (
          <fieldset className="space-y-2" data-testid="auth-kind-selector">
            <legend className="text-sm font-medium">Como deseja criar sua conta?</legend>
            {kinds.map((k) => (
              <label key={k.id} className={`ga-kind-option ${kind === k.id ? "is-selected" : ""} ${k.soon ? "is-disabled" : ""}`} data-testid={`auth-kind-option-${k.id}`}>
                <input type="radio" name="kind" value={k.id} checked={kind === k.id} disabled={k.soon} onChange={() => setKind(k.id)} className="h-4 w-4 shrink-0 accent-[#13C9A3]" data-testid={`auth-kind-${k.id}`} />
                <span className="min-w-0 flex-1 whitespace-normal break-words">{k.label}</span>{k.soon && <span className="ga-kind-soon shrink-0">Em breve</span>}
              </label>
            ))}
          </fieldset>
        )}

        {isUserReg && (
          <Field id="username" label="Nome de usuário" value={form.username} onChange={(v) => checkUsername(v.toLowerCase().replace(/[^a-z0-9_.]/g, "").slice(0, 20))} placeholder="seu.nome" testId="auth-username-input" autoComplete="username" />
        )}
        {isUserReg && form.username.length >= 3 && userAvail !== null && <p className={`-mt-2 text-xs ${userAvail ? "text-emerald-700" : "text-red-600"}`} data-testid="auth-username-availability">{userAvail ? "Nome disponível." : "Este nome já está em uso."}</p>}
        {mode === "recovery" && <Field id="username" label="Nome de usuário" value={form.username} onChange={(v) => set("username")(v.toLowerCase().replace(/[^a-z0-9_.@]/g, ""))} placeholder="seu.nome" testId="auth-username-input" autoComplete="username" />}
        {mode === "recovery" && <Field id="recovery" label="Código de recuperação" value={form.recoveryCode} onChange={(v) => set("recoveryCode")(v.toUpperCase())} placeholder="XXXXX-XXXXX" testId="auth-recovery-code-input" autoComplete="one-time-code" />}
        {mode === "register" && !isUserReg && <Field id="name" label="Seu nome" value={form.name} onChange={set("name")} placeholder="Como quer ser chamado?" testId="auth-name-input" autoComplete="name" />}
        {mode === "login" && <Field id="email" label="E-mail ou nome de usuário" value={form.email} onChange={set("email")} placeholder="voce@email.com ou seu.nome" testId="auth-email-input" autoComplete="username" />}
        {(mode === "forgot" || (mode === "register" && !isUserReg)) && <Field id="email" label="E-mail" type="email" value={form.email} onChange={set("email")} placeholder="voce@email.com" testId="auth-email-input" autoComplete="email" />}
        {mode !== "forgot" && <Field id="password" label={mode === "reset" || mode === "recovery" ? "Nova senha" : "Senha"} type="password" value={form.password} onChange={set("password")} placeholder={isUserReg || mode === "recovery" ? "Mínimo 8 caracteres, letras e números" : "Mínimo 6 caracteres"} testId="auth-password-input" autoComplete={mode === "login" ? "current-password" : "new-password"} />}
        {(mode === "reset" || mode === "recovery" || isUserReg) && <Field id="confirm" label={isUserReg ? "Confirmar senha" : "Confirmar nova senha"} type="password" value={form.confirm} onChange={set("confirm")} placeholder="Repita a senha" testId="auth-password-confirm-input" autoComplete="new-password" />}

        {isUserReg && (
          <div className="rounded-2xl p-3 text-xs" style={{ background: "#FFF4E5", color: "#7A4A00" }} data-testid="auth-no-recovery-warning">
            <p><b>Atenção:</b> ao criar sua conta sem e-mail ou telefone, você poderá perder permanentemente o acesso caso esqueça sua senha. Essa modalidade também oferece menor proteção contra roubo de credenciais do que a autenticação em duas etapas. Depois de entrar, você poderá gerar códigos de recuperação ou adicionar um e-mail em Perfil → Segurança.</p>
            <label className="mt-2 flex items-start gap-2.5"><input type="checkbox" checked={form.noRecovery} onChange={(e) => set("noRecovery")(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#13C9A3]" data-testid="auth-no-recovery-checkbox" /><span>Entendi e quero continuar assim mesmo.</span></label>
          </div>
        )}

        {mode === "register" && (
          <div className="space-y-2 text-sm">
            <label className="flex items-start gap-2.5">
              <input type="checkbox" checked={form.terms} onChange={(e) => set("terms")(e.target.checked)} className="mt-1 h-4 w-4 accent-[#13C9A3]" data-testid="auth-terms-checkbox" />
              <span>Li e aceito os <Link to="/termos" className="font-medium" style={{ color: "#0A6373" }}>Termos de uso</Link>.</span>
            </label>
            <label className="flex items-start gap-2.5">
              <input type="checkbox" checked={form.privacy} onChange={(e) => set("privacy")(e.target.checked)} className="mt-1 h-4 w-4 accent-[#13C9A3]" data-testid="auth-privacy-checkbox" />
              <span>Li e aceito a <Link to="/politica-de-privacidade" className="font-medium" style={{ color: "#0A6373" }} data-testid="auth-privacy-link">Política de privacidade</Link>.</span>
            </label>
          </div>
        )}

        {mode === "forgot" && notice && (
          <div className="rounded-2xl p-3 text-sm" role="status" style={notice.ok ? { background: "#E6F7F1", color: "#05324A" } : { background: "#FFF4E5", color: "#7A4A00" }} data-testid="auth-forgot-notice">{notice.text}</div>
        )}
        {mode === "forgot" && demoToken && (
          <div className="ga-card p-4 text-sm" data-testid="auth-demo-reset-box">
            <p className="ga-muted text-xs">Envio de e-mail ainda não configurado (demonstração). Use o botão abaixo para redefinir agora:</p>
            <button type="button" onClick={() => setMode("reset")} className="mt-2 font-semibold" style={{ color: "#0A6373" }} data-testid="auth-demo-reset-link">Redefinir minha senha</button>
          </div>
        )}

        <button type="submit" disabled={busy} className="ga-btn-primary w-full text-base" data-testid="auth-submit-button">
          {busy ? "Aguarde..." : { login: "Entrar", register: "Criar conta", forgot: "Enviar link", reset: "Salvar nova senha", recovery: "Redefinir senha" }[mode]}
        </button>

        <div className="space-y-2 pt-2 text-center text-sm">
          {mode === "login" && (
            <>
              <button type="button" onClick={() => setMode("forgot")} className="ga-muted" data-testid="auth-forgot-link">Esqueci minha senha</button>
              <p>Ainda não tem conta? <button type="button" onClick={() => setMode("register")} className="font-semibold" style={{ color: "#0A6373" }} data-testid="auth-tab-register">Cadastre-se</button></p>
            </>
          )}
          {mode === "forgot" && <p className="ga-muted text-xs">Conta sem e-mail? <button type="button" onClick={() => setMode("recovery")} className="font-semibold" style={{ color: "#0A6373" }} data-testid="auth-recovery-link">Usar código de recuperação</button></p>}
          {mode !== "login" && (
            <p><button type="button" onClick={() => setMode("login")} className="font-semibold" style={{ color: "#0A6373" }} data-testid="auth-tab-login">Voltar para o login</button></p>
          )}
          <p className="ga-muted pt-1 text-xs"><Link to="/termos" className="underline" data-testid="auth-footer-terms">Termos de Uso</Link> · <Link to="/politica-de-privacidade" className="underline" data-testid="auth-footer-privacy">Política de Privacidade</Link></p>
        </div>
        {mode === "login" && <div className="pt-2" data-testid="auth-install-app"><InstallAppButton /></div>}
        <p className="ga-muted pb-8 pt-4 text-center text-xs">Suas memórias são privadas. Só você tem acesso.</p>
      </form>
    </div>
  );
}
